import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import SFChipList from "../../chip/chipList";
import SFPopover from "../../popover";
import { SFStack } from "../../grid";
import SFChip from "../../chip";
import SFTypography from "../../typography";
import { SFCloseIcon } from "../../icons";
import getStyled from "../../../hooks/getStyled";
import { CheckboxFilterPropTypes } from "./checkboxFilter";
import ActionBtns from "../filterChips/actionBtns";

const { styled } = getStyled();

const SFPopoverStyled = styled(SFPopover)(() => ({
  "& .MuiPopover-paper": {
    boxShadow: "none",
  },
}));

const FilterChipList = ({ checkboxFilter, onChange, onEditFilters }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverItems, setPopoverItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);
  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - 8);
      }
    };

    handleResize();

     
    window.addEventListener("resize", handleResize);

    return () => {
       
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  // Helper function to uncheck all checkboxes
  const uncheckAllCheckboxes = () => {
    if (!Array.isArray(checkboxFilter?.checkboxes)) return;

    // Create array of all checkboxes with checked=false
    const uncheckedCheckboxes = checkboxFilter.checkboxes.map((checkbox) => ({
      ...checkbox,
      checked: false,
    }));
    if (onChange) {
      onChange({
        type: "checkbox",
        isBulkOperation: true, // Using a boolean flag instead of null value
        updatedCheckboxes: uncheckedCheckboxes,
      });
    }
  };
  // Handle individual checkbox deletion from popover
  const handleCheckboxDelete = (checkboxValue) => {
    // Update the specific checkbox and provide the full updated state
    if (onChange) {
      const updatedCheckboxes = checkboxFilter.checkboxes.map((checkbox) => {
        const value = checkbox.value || checkbox.label;
        if (value === checkboxValue) {
          return { ...checkbox, checked: false };
        }
        return checkbox;
      });
      onChange({
        type: "checkbox",
        checkboxValue,
        checked: false,
        isBulkOperation: false, // This is a single checkbox change
        updatedCheckboxes,
      });
    }

    // Update popover items to remove this item
    setPopoverItems((prevItems) =>
      prevItems.filter((item) => item.value !== checkboxValue)
    );

    // Close popover if no items left
    if (popoverItems.length <= 1) {
      setAnchorEl(null);
    }
  };

  // Expand filter values when clicking on a chip
  const expandFilterValues = (e) => {
    if (!Array.isArray(checkboxFilter?.checkboxes)) return;

    // Get all checked checkboxes
    const checkedBoxes = checkboxFilter.checkboxes.filter(
      (checkbox) => checkbox.checked === true
    );

    if (checkedBoxes.length === 0) return;

    // Create popover items from checked boxes
    const items = checkedBoxes.map((checkbox) => ({
      label: checkbox.label,
      value: checkbox.value || checkbox.label,
    }));

    setPopoverItems(items);
    setAnchorEl(e.target.offsetParent);
  };

  // Get selected checkbox items for the chip list
  const getSelectedCheckboxItems = () => {
    const selectedItems = [];

    // Add checkbox items
    if (
      checkboxFilter &&
      Array.isArray(checkboxFilter.checkboxes) &&
      checkboxFilter.checkboxes.length > 0
    ) {
      // Find all checked checkboxes
      const checkedBoxes = checkboxFilter.checkboxes.filter(
        (checkbox) => checkbox.checked === true
      );

      // Only create a chip if there are checked boxes
      if (checkedBoxes.length === 0) return selectedItems;

      const groupLabel = checkboxFilter.label;
      let chipValue;

      // Determine value based on number of checked boxes
      if (checkedBoxes.length === 1) {
        // If only one checkbox is selected, show its label
        chipValue = checkedBoxes[0].label;
      } else {
        // If multiple checkboxes are selected, show the count
        chipValue = `${checkedBoxes.length}`;
      }

      // Create one chip for the entire filter
      selectedItems.push({
        label: groupLabel,
        value: chipValue,
        // When deleted, uncheck all checkboxes
        onDelete: () => uncheckAllCheckboxes(),
        // Add click handler to open popover with detailed items
        onClick: (e) => expandFilterValues(e),
        // Flag to indicate this has multiple values that can be shown in popover
        multiple: checkedBoxes.length > 1,
      });
    }
    return selectedItems;
  };
  const selectedCheckboxItems = getSelectedCheckboxItems();

  if (selectedCheckboxItems.length === 0) {
    return null;
  } // Handler for clearing all checkbox filters
  const clearAllCheckboxFilters = () => {
    // Reset checkboxes if they exist
    if (
      checkboxFilter?.checkboxes &&
      Array.isArray(checkboxFilter.checkboxes)
    ) {
      // Create array of all checkboxes with checked=false
      const uncheckedCheckboxes = checkboxFilter.checkboxes.map((checkbox) => ({
        ...checkbox,
        checked: false,
      }));
      if (onChange) {
        onChange({
          type: "checkbox", // Change from "clear" to "checkbox"
          isBulkOperation: true,
          checked: false,
          updatedCheckboxes: uncheckedCheckboxes,
          // Don't include dropdownValues to avoid affecting dropdown selection
        });
      }
    }
  };

  // Determine which chips to display based on container width and showAll state
  const getVisibleItems = () => {
    if (showAll || !containerWidth) {
      return selectedCheckboxItems;
    }

    // Constants for width calculation (adjust as needed)
    const chipPadding = 16;
    const chipMargin = 8;
    const clearBtnWidth = 60;
    const editFiltersBtnWidth = 85;
    const collapsedChipsBtnWidth = 55;
    let remainingSpace =
      containerWidth -
      clearBtnWidth -
      collapsedChipsBtnWidth -
      editFiltersBtnWidth;
    let visibleItems = [];
    let collapsedItemsCount = 0;

    for (const item of selectedCheckboxItems) {
      // Approximate width calculation for each chip
      const chipWidth =
        (item.label.length + String(item.value).length) * 8 +
        chipPadding +
        chipMargin;

      if (chipWidth <= remainingSpace) {
        visibleItems.push(item);
        remainingSpace -= chipWidth;
      } else {
        collapsedItemsCount++;
      }
    }

    // Add the show more chip if there are collapsed items
    if (collapsedItemsCount > 0) {
      visibleItems.push({
        key: "collapsedItemsCount",
        label: `+${collapsedItemsCount}`,
        onClick: () => setShowAll(true),
      });
    }

    return visibleItems;
  };

  const visibleItems = getVisibleItems();

  return (
    <>
      <div ref={containerRef}>
        <SFChipList
          items={visibleItems}
          variant="filterChip"
          size="small"
          customActions={
            // The clearAllCheckboxFilters function is used here to only clear checkbox filters, not dropdown filters
            <ActionBtns
              onEditFilters={onEditFilters}
              onDeleteAll={clearAllCheckboxFilters}
              {...(showAll && { onShowLess: () => setShowAll(false) })}
            />
          }
        />
      </div>
      <SFPopoverStyled
        anchorEl={anchorEl}
        position="bottom-start"
        onClose={() => setAnchorEl(null)}
      >
        <SFStack>
          {popoverItems.map((item) => (
            <SFChip
              key={item.value}
              size="small"
              variant="popoverFilterChip"
              onDelete={() => handleCheckboxDelete(item.value)}
              deleteIcon={<SFCloseIcon />}
            >
              <SFTypography variant="textLabel">{item.label}</SFTypography>
            </SFChip>
          ))}
        </SFStack>
      </SFPopoverStyled>
    </>
  );
};

/**
 * PropTypes definition for the FilterChipList component.
 * This component displays chips representing selected filter options and provides
 * functionality to remove individual filters or edit the filter selection.
 */
FilterChipList.propTypes = {
  /**
   * Configuration for the dropdown filter.
   * Optional - if not provided, no dropdown filter chips will be displayed.
   */
  dropdownFilter: PropTypes.shape({
    /**
     * Placeholder text for the dropdown.
     */
    placeholder: PropTypes.string,

    /**
     * Array of currently selected values in the dropdown.
     */
    selectedValues: PropTypes.arrayOf(PropTypes.string),

    /**
     * Array of all available options for the dropdown.
     */
    values: PropTypes.arrayOf(PropTypes.string),
  }),

  /**
   * Configuration for the checkbox filters.
   * Optional - if not provided, no checkbox filter chips will be displayed.
   * @see CheckboxFilterPropTypes for detailed documentation.
   */
  checkboxFilter: PropTypes.shape(CheckboxFilterPropTypes),

  /**
   * Unified change handler for checkbox filters in the chip list.
   * Called when deleting individual checkboxes or clearing all checkbox filters.
   * See SFPanelSearch component for detailed documentation of the handler structure.
   */
  onChange: PropTypes.func,

  /**
   * Handler called when the edit filters button is clicked.
   * Used to expand the filter panel.
   */
  onEditFilters: PropTypes.func,
};

export default FilterChipList;
